import { distance, type Quad } from './geometry';

/**
 * Perspektivisches Geraderücken auf Canvas – aus dem schräg abfotografierten
 * Trapez wird ein Rechteck.
 *
 * Gerechnet wird *rückwärts*: für jedes Pixel des Ergebnisses wird gefragt,
 * woher es im Quellbild kommt, und dort bilinear abgetastet. Das ist der
 * einzige Weg, der keine Löcher hinterlässt – bei der Vorwärtsrichtung würde
 * das gestreckte Bild lückenhaft.
 */

/** Obergrenzen für das Ergebnis. „large" im Archiv ist 2048 px, das genügt. */
export const EXPORT_LIMITS = { maxEdge: 2600, maxPixels: 4_500_000 } as const;

/**
 * So groß wird das Quellbild eingelesen. Ein 12-Megapixel-Handyfoto als
 * ImageData wären knapp 50 MB; bei 3000 px Kantenlänge bleiben es rund 27 MB
 * und die Auflösung reicht immer noch weit über das Ergebnis hinaus. Auf
 * iOS-Safari ist die Canvas-Fläche außerdem hart begrenzt.
 */
export const SOURCE_MAX_EDGE = 3000;

export function loadImageFile(file: File): Promise<{ image: HTMLImageElement; revoke: () => void }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    const revoke = () => URL.revokeObjectURL(url);
    image.onload = () => resolve({ image, revoke });
    image.onerror = () => {
      revoke();
      reject(new Error('Bild konnte nicht gelesen werden'));
    };
    image.src = url;
  });
}

/**
 * Bild als Pixelfeld, höchstens `maxEdge` groß.
 *
 * `drawImage` wendet die EXIF-Orientierung an – genauso wie das `<img>` in der
 * Anzeige. Deshalb passen Vorschau und Ergebnis zusammen, ohne dass hier
 * irgendwo selbst gedreht werden müsste.
 */
export function imageDataFrom(image: HTMLImageElement, maxEdge: number): ImageData {
  const iw = image.naturalWidth || image.width;
  const ih = image.naturalHeight || image.height;
  const scale = Math.min(1, maxEdge / Math.max(iw, ih));
  const w = Math.max(1, Math.round(iw * scale));
  const h = Math.max(1, Math.round(ih * scale));

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('Canvas nicht verfügbar');
  ctx.drawImage(image, 0, 0, w, h);
  return ctx.getImageData(0, 0, w, h);
}

/**
 * Projektive Abbildung Einheitsquadrat → Viereck (nach Paul Heckbert,
 * „Fundamentals of Texture Mapping and Image Warping").
 *
 * Ergebnis sind die acht Koeffizienten von
 *   x = (a·u + b·v + c) / (g·u + h·v + 1)
 *   y = (d·u + e·v + f) / (g·u + h·v + 1)
 * mit (u,v) ∈ [0,1]², wobei (0,0) auf die erste Ecke des Vierecks fällt und
 * (1,0) auf die zweite. Der Sonderfall ohne perspektivischen Anteil (das
 * Viereck ist ein Parallelogramm) muss getrennt behandelt werden, weil dort
 * eine Determinante verschwindet.
 */
export function unitSquareToQuad(quad: Quad): {
  a: number;
  b: number;
  c: number;
  d: number;
  e: number;
  f: number;
  g: number;
  h: number;
} {
  const [p0, p1, p2, p3] = quad;

  const sx = p0.x - p1.x + p2.x - p3.x;
  const sy = p0.y - p1.y + p2.y - p3.y;

  if (Math.abs(sx) < 1e-9 && Math.abs(sy) < 1e-9) {
    return {
      a: p1.x - p0.x,
      b: p3.x - p0.x,
      c: p0.x,
      d: p1.y - p0.y,
      e: p3.y - p0.y,
      f: p0.y,
      g: 0,
      h: 0,
    };
  }

  const dx1 = p1.x - p2.x;
  const dx2 = p3.x - p2.x;
  const dy1 = p1.y - p2.y;
  const dy2 = p3.y - p2.y;

  const den = dx1 * dy2 - dx2 * dy1;
  const g = (sx * dy2 - dx2 * sy) / den;
  const h = (dx1 * sy - sx * dy1) / den;

  return {
    a: p1.x - p0.x + g * p1.x,
    b: p3.x - p0.x + h * p3.x,
    c: p0.x,
    d: p1.y - p0.y + g * p1.y,
    e: p3.y - p0.y + h * p3.y,
    f: p0.y,
    g,
    h,
  };
}

/** Zielmaße: die längere der beiden gegenüberliegenden Seiten gibt das Maß vor. */
export function outputSize(
  source: { width: number; height: number },
  quad: Quad,
  limits: { maxEdge: number; maxPixels: number } = EXPORT_LIMITS,
): { width: number; height: number } {
  const px = quad.map((p) => ({ x: p.x * source.width, y: p.y * source.height })) as Quad;
  const width = Math.max(distance(px[0], px[1]), distance(px[3], px[2]));
  const height = Math.max(distance(px[0], px[3]), distance(px[1], px[2]));

  const scale = Math.min(
    1,
    limits.maxEdge / Math.max(width, height),
    Math.sqrt(limits.maxPixels / Math.max(1, width * height)),
  );

  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/**
 * Entzerrt den Ausschnitt in eine neue Canvas.
 *
 * `quad` ist normalisiert (0…1) und in der Reihenfolge TL, TR, BR, BL – die
 * erste Ecke landet also oben links im Ergebnis. Eine Vierteldrehung erreicht
 * man, indem die Ecken vorher durchgeschoben werden (`rotateQuad`).
 */
export function warpQuad(
  source: ImageData,
  quad: Quad,
  limits: { maxEdge: number; maxPixels: number } = EXPORT_LIMITS,
): HTMLCanvasElement {
  const sw = source.width;
  const sh = source.height;
  const px = quad.map((p) => ({ x: p.x * sw, y: p.y * sh })) as Quad;
  const { width: outW, height: outH } = outputSize(source, quad, limits);

  const m = unitSquareToQuad(px);
  const src = source.data;
  const out = new Uint8ClampedArray(outW * outH * 4);

  for (let y = 0; y < outH; y++) {
    const v = (y + 0.5) / outH;
    // Der v-Anteil ist pro Zeile konstant – einmal vorrechnen spart in der
    // inneren Schleife drei Multiplikationen pro Pixel.
    const bx = m.b * v + m.c;
    const by = m.e * v + m.f;
    const bw = m.h * v + 1;

    for (let x = 0; x < outW; x++) {
      const u = (x + 0.5) / outW;
      const den = m.g * u + bw;
      const fx = (m.a * u + bx) / den;
      const fy = (m.d * u + by) / den;

      // Bilinear, mit Klemmen am Rand statt Sprung ins Schwarze.
      const x0 = Math.floor(fx);
      const y0 = Math.floor(fy);
      const tx = fx - x0;
      const ty = fy - y0;
      const cx0 = x0 < 0 ? 0 : x0 > sw - 1 ? sw - 1 : x0;
      const cy0 = y0 < 0 ? 0 : y0 > sh - 1 ? sh - 1 : y0;
      const cx1 = x0 + 1 < 0 ? 0 : x0 + 1 > sw - 1 ? sw - 1 : x0 + 1;
      const cy1 = y0 + 1 < 0 ? 0 : y0 + 1 > sh - 1 ? sh - 1 : y0 + 1;

      const i00 = (cy0 * sw + cx0) * 4;
      const i10 = (cy0 * sw + cx1) * 4;
      const i01 = (cy1 * sw + cx0) * 4;
      const i11 = (cy1 * sw + cx1) * 4;

      const w00 = (1 - tx) * (1 - ty);
      const w10 = tx * (1 - ty);
      const w01 = (1 - tx) * ty;
      const w11 = tx * ty;

      const o = (y * outW + x) * 4;
      out[o] = src[i00]! * w00 + src[i10]! * w10 + src[i01]! * w01 + src[i11]! * w11;
      out[o + 1] =
        src[i00 + 1]! * w00 + src[i10 + 1]! * w10 + src[i01 + 1]! * w01 + src[i11 + 1]! * w11;
      out[o + 2] =
        src[i00 + 2]! * w00 + src[i10 + 2]! * w10 + src[i01 + 2]! * w01 + src[i11 + 2]! * w11;
      out[o + 3] = 255;
    }
  }

  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas nicht verfügbar');
  ctx.putImageData(new ImageData(out, outW, outH), 0, 0);
  return canvas;
}

export function canvasToBlob(canvas: HTMLCanvasElement, quality = 0.92): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Bild konnte nicht erzeugt werden'))),
      'image/jpeg',
      quality,
    );
  });
}

/** Dateiname mit Zeitstempel – abfotografierte Seiten bleiben so sortierbar. */
export function scanFilename(date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return [
    'scan-',
    date.getFullYear(),
    pad(date.getMonth() + 1),
    pad(date.getDate()),
    '-',
    pad(date.getHours()),
    pad(date.getMinutes()),
    pad(date.getSeconds()),
    '.jpg',
  ].join('');
}
